/**
 * Needle Inspector Agent - Entry Point
 *
 * This script is designed to be injected into any web page with a Three.js scene.
 * It auto-detects Three.js, boots minimal inspector services, and exposes a
 * programmatic API at window.__NEEDLE_INSPECTOR__ for AI agents.
 *
 * Usage from Playwright:
 *   await page.addScriptTag({ url: 'https://inspector.needle.tools/agent.js' });
 *   await page.waitForFunction(() => window.__NEEDLE_INSPECTOR__?.ready);
 *   const tools = await page.evaluate(() => window.__NEEDLE_INSPECTOR__.getTools());
 */

import { setupThreeDetection, type ThreeDetectionHost } from "./detect.js";
import { startupAgent } from "./services.js";
import { createInspectorAPI, type NeedleInspectorAPI } from "./api.js";
import { attachLiveInspection } from "./liveBridge.js";
import { createLiveInspectionActivity } from "./liveActivity.js";
import { runtimeUrlForAgent } from "./liveRuntimeUrl.js";
import { registerWebMcpTools, type WebMcpTool } from "../../core/src/editor/services/mcp/browser/webmcp.js";
import type { IApplication } from "../../core/src/editor/services/application/common/application.js";

const emptyProperties = () => ({ totalReturned: 0, totalMatched: 0, hasMore: false, results: [] });
const liveScript = document.currentScript as HTMLScriptElement | null;
const liveSession = liveScript?.dataset.needleSession ?? null;
const liveUi = Boolean(liveSession && liveScript?.dataset.needleUi === "true");
const liveUrl = liveSession && liveScript?.src ? new URL(`/live/${liveSession}`, liveScript.src) : null;
if (liveUrl) liveUrl.protocol = liveUrl.protocol === "https:" ? "wss:" : "ws:";
const liveBridgeKey = Symbol.for("needle.inspector.liveBridge");
const liveActivityKey = Symbol.for("needle.inspector.liveActivity");
const liveHolder = window as unknown as Record<symbol, { session: string; dispose(): void } | undefined>;

if (!window.__NEEDLE_INSPECTOR__) startAgent();
else if (liveUrl) {
    const existing = window.__NEEDLE_INSPECTOR__;
    if (existing.ready) connectLive(existing);
    else {
        const timer = setInterval(() => {
            if (!existing.ready) return;
            clearInterval(timer);
            connectLive(existing);
        }, 100);
        setTimeout(() => clearInterval(timer), 30_000);
    }
}

function connectLive(api: NeedleInspectorAPI): void {
    if (!liveSession || !liveUrl) return;
    const previous = liveHolder[liveBridgeKey];
    if (previous?.session === liveSession) return;
    previous?.dispose();
    const connection = attachLiveInspection(liveSession, api, () => new WebSocket(liveUrl), async () => {
        try {
            const statusUrl = new URL(liveUrl);
            statusUrl.protocol = statusUrl.protocol === "wss:" ? "https:" : "http:";
            const response = await fetch(statusUrl, { cache: "no-store" });
            if (response.status === 410) {
                console.warn("[Needle Inspector Agent] Live inspection session expired. Start a new session in ChatGPT.");
                return false;
            }
            if (!response.ok) console.warn(`[Needle Inspector Agent] Session check failed (${response.status}); retrying connection.`);
        } catch (error) {
            console.warn("[Needle Inspector Agent] Session check failed; retrying connection.", error);
        }
        return true;
    }, () => {
        const activity = (api as unknown as Record<symbol, (() => boolean) | undefined>)[liveActivityKey];
        return activity ? activity() : document.visibilityState === 'visible';
    });
    if (connection) liveHolder[liveBridgeKey] = { session: liveSession, dispose: () => connection.dispose() };
}

async function mountLiveWorkbench(host: ThreeDetectionHost): Promise<IApplication> {
    if (!liveScript?.src) throw new Error("Live Inspector UI requires the agent script URL");
    const runtimeUrl = runtimeUrlForAgent(liveScript.src);
    const runtime = await import(/* @vite-ignore */ runtimeUrl) as {
        mountAgentInspector(host: { scene: object; renderer: object }, auth: { origin: string; session: string }): Promise<{ application: IApplication }>;
    };
    const auth = { origin: new URL(liveScript.src).origin, session: liveSession! };
    const session = await runtime.mountAgentInspector({ scene: host.scenes[0], renderer: host.renderers[0] }, auth);
    return session.application;
}

function startAgent(): void {
    // Start detection immediately — must happen before Three.js initializes
    const host = setupThreeDetection();

// Create a placeholder API that agents can detect
const api: NeedleInspectorAPI = {
    ready: false,
    getTools: async () => [],
    callTool: async () => ({ error: "Inspector not ready yet. Wait for ready === true." }),
    getHierarchy: async () => [],
    openResource: async () => ({ success: false, error: "Inspector not ready yet. Wait for ready === true." }),
    getSelectedNodes: async () => [],
    selectNode: async () => ({ success: false, message: "Inspector not ready yet." }),
    searchNodes: async () => [],
    getProperties: async () => emptyProperties(),
    findProperties: async () => emptyProperties(),
    readProperty: async () => ({ error: "Inspector not ready yet." }),
};

// Add runtime help for agents
(api as any).usage = "IMPORTANT: All methods are async. You MUST use 'await'. Example: const tree = await api.getHierarchy()";
(api as any).docs = "https://inspector.needle.tools/agent.md";

    window.__NEEDLE_INSPECTOR__ = api;

/**
 * Initialize the inspector once a Three.js scene is detected.
 */
    async function initialize(host: ThreeDetectionHost) {
    try {
        const app = liveUi ? await mountLiveWorkbench(host) : await startupAgent();
        const engine = app.webgl?.getEngine("threejs") as { observe?(object: unknown): void } | undefined;

        // Register detected scenes/renderers with the project service
        if (app.project) {
            for (const renderer of host.renderers) {
                if (liveUi && renderer === host.renderers[0]) continue;
                engine?.observe?.(renderer);
                app.project.add(renderer);
            }
            for (const scene of host.scenes) {
                if (liveUi && scene === host.scenes[0]) continue;
                engine?.observe?.(scene);
                app.project.add(scene);
            }
        }
        for (const camera of host.cameras) engine?.observe?.(camera);

        // Listen for new scenes/renderers added after initialization
        host.onChanged(evt => {
            if (evt.type === "added") {
                engine?.observe?.(evt.object);
                if (app.project) app.project.add(evt.object);
            }
        });

        // Create and expose the full API
        const analytics = (app as typeof app & { analytics?: Parameters<typeof createInspectorAPI>[1] }).analytics;
        const fullApi = createInspectorAPI(app, analytics);

        // Install the real methods before registering them. A WebMCP host may
        // invoke a tool as soon as registerTool() resolves.
        Object.assign(api, fullApi);
        (api as unknown as Record<symbol, () => boolean>)[liveActivityKey] = createLiveInspectionActivity(app, document, window);

        const webMcpTools: WebMcpTool[] = (await api.getTools()).map(tool => ({
            ...tool,
            execute: args => api.callTool(tool.name, args),
        }));
        const webMcpRegistration = new AbortController();
        const registration = await registerWebMcpTools(window, webMcpTools, webMcpRegistration.signal);
        for (const frame of registration.frames) {
            for (const failure of frame.failed) {
                console.warn(`[Needle Inspector Agent] WebMCP refused tool '${failure.name}':`, failure.reason);
            }
        }

        api.ready = true;

        connectLive(api);

        // Track agent initialization
        analytics?.logEvent("agent.ready", { host: window.location.host });

        console.log("[Needle Inspector Agent] Ready. Use window.__NEEDLE_INSPECTOR__ to inspect the scene.");
    } catch (err) {
        console.error("[Needle Inspector Agent] Failed to initialize:", err);
    }
    }


// Wait for Three.js scenes to appear, then initialize
    let initialized = false;

    function tryInitialize() {
        if (initialized) return;
        if (liveUi ? host.scenes.length > 0 && host.renderers.length > 0
            : host.scenes.length > 0 || host.renderers.length > 0) {
            initialized = true;
            initialize(host);
        }
    }

// Check if scenes are already detected (script loaded after Three.js)
    tryInitialize();

// Also listen for future scene additions
    host.onChanged(() => tryInitialize());

// Fallback: keep checking for a while in case Three.js loads lazily
    const checkInterval = setInterval(() => {
        tryInitialize();
        if (initialized) {
            clearInterval(checkInterval);
        }
    }, 500);

    // Stop checking after 30 seconds
    setTimeout(() => {
        clearInterval(checkInterval);
        if (!initialized) {
            console.warn("[Needle Inspector Agent] No Three.js scene detected after 30 seconds. The inspector will activate if a scene is added later.");
        }
    }, 30_000);
}
